// Theme-specific sounds from myinstants.com, verified to resolve.
// Played via HTML5 <audio> element (not Web Audio API) so no CORS is needed —
// the browser fetches and plays the mp3 directly, like any <audio> tag.

export type ThemeSound = {
    /** myinstants.com direct mp3 URL */
    url: string;
    /** Short label shown in UI */
    label: string;
    /** Emoji icon */
    emoji: string;
};

const MI = 'https://www.myinstants.com/media/sounds';

export const THEME_SOUNDS: Record<string, ThemeSound[]> = {
    'anime-dragonball-z': [
        { url: `${MI}/kamehameha.swf.mp3`, label: 'Kamehameha', emoji: '🐲' },
        { url: `${MI}/goku-kamehameha-br.mp3`, label: 'Goku Kamehameha', emoji: '💥' },
        { url: `${MI}/kamehameha-wave-sound-effect.mp3`, label: 'Kamehameha Wave', emoji: '⚡' },
    ],
    'anime-buu': [
        { url: `${MI}/majin-buu.mp3`, label: 'Majin Buu', emoji: '🍭' },
        { url: `${MI}/gohan-kamehameha.mp3`, label: 'Gohan Kamehameha', emoji: '💥' },
    ],
    'anime-conan': [
        { url: `${MI}/conan.mp3`, label: 'Conan', emoji: '🔍' },
        { url: `${MI}/ah-le-le.mp3`, label: 'Ah Le Le', emoji: '🤔' },
    ],
    'anime-beyblade-x': [
        { url: `${MI}/461-beyblade.mp3`, label: 'Beyblade', emoji: '🌀' },
        { url: `${MI}/beyblade-321-go-shot.mp3`, label: 'Go Shoot!', emoji: '🚀' },
    ],
    'anime-tsubasa': [
        { url: `${MI}/tsubasa.mp3`, label: 'Tsubasa', emoji: '⚽' },
        { url: `${MI}/supercampeoes-capitan-tsubasa-2002-intro.mp3`, label: 'Intro Theme', emoji: '🎵' },
    ],
    'anime-kuroko': [
        { url: `${MI}/knb.mp3`, label: 'Kuroko no Basuke', emoji: '🏀' },
        { url: `${MI}/kuroko-absolutezxc.mp3`, label: 'Kuroko!', emoji: '🔵' },
    ],
    'anime-slam-dunk': [
        { url: `${MI}/loud-dunk.mp3`, label: 'Slam Dunk!', emoji: '🏀' },
        { url: `${MI}/0003.mp3`, label: 'Slam Dunk Theme', emoji: '🎵' },
    ],
    'anime-pokemon': [
        { url: `${MI}/025-kanto-pikachu.mp3`, label: 'Pikachu!', emoji: '⚡' },
        { url: `${MI}/e-o-pikachu.mp3`, label: 'Pikachu', emoji: '🐭' },
    ],
    'anime-naruto': [
        { url: `${MI}/naruto-minato-o-supremo-rasengan-taikyoku-rasengan.mp3`, label: 'Rasengan', emoji: '🍥' },
        { url: `${MI}/epic-goku-kamehameha-soundtrack_70k.mp3`, label: 'Epic Soundtrack', emoji: '🎵' },
    ],
    'anime-onepiece': [
        { url: `${MI}/doflamingo-laugh.mp3`, label: 'Doflamingo Laugh', emoji: '🏴‍☠️' },
        { url: `${MI}/franky-supeeer.mp3`, label: 'Franky Super!', emoji: '🤖' },
        { url: `${MI}/crocodile-mugiwara.mp3`, label: 'Mugiwara!', emoji: '⛵' },
    ],
    'anime-demonslayer': [
        { url: `${MI}/bu-ses-demon-slayer-4-b.mp3`, label: 'Demon Slayer', emoji: '⚔️' },
        { url: `${MI}/ara-ara-sayonara-mp3cut.mp3`, label: 'Ara Ara', emoji: '😮' },
    ],
    'anime-mha': [
        { url: `${MI}/all-might-plus-ultra-japanese.mp3`, label: 'Plus Ultra!', emoji: '💥' },
        { url: `${MI}/all-might-plus-ultra-punch-sound-effect-english-version.mp3`, label: 'Plus Ultra Punch', emoji: '👊' },
    ],
    'anime-doraemon': [
        { url: `${MI}/01-esiiyngepidt-n.mp3`, label: 'Doraemon Theme', emoji: '🤖' },
        { url: `${MI}/05-esiiyngy-ng_oFV0gZG.mp3`, label: 'Doraemon', emoji: '🔔' },
    ],
    'anime-shinchan': [
        { url: `${MI}/crayon-shin-chan-laugh.mp3`, label: 'Shin Chan Laugh', emoji: '👦' },
        { url: `${MI}/bebesitashinchan1.mp3`, label: 'Shin Chan', emoji: '😄' },
    ],
    'anime-jujutsu': [
        { url: `${MI}/black-flash-gojos.mp3`, label: 'Black Flash', emoji: '👁️' },
    ],
    'anime-spyfamily': [
        // myinstants results were poor for spy family; fallback to kid sounds
    ],
    'anime-haikyuu': [
        { url: `${MI}/baki_baki.mp3`, label: 'Haikyuu', emoji: '🏐' },
        { url: `${MI}/arara-gomen-tsukishima-kei-edit.mp3`, label: 'Gomen!', emoji: '😅' },
    ],
    'anime-cars': [
        { url: `${MI}/ahi-esta-el-rayo-mcqueen.mp3`, label: 'El Rayo McQueen', emoji: '🏎️' },
    ],
    'anime-marvel-spiderman': [
        { url: `${MI}/aint-my-problem-spiderman.mp3`, label: 'Not My Problem', emoji: '🕷️' },
    ],
};

// HTML5 Audio element for theme sound playback (no CORS needed).
let audioEl: HTMLAudioElement | null = null;

const getAudioEl = (): HTMLAudioElement => {
    if (!audioEl) {
        audioEl = new Audio();
        audioEl.preload = 'none';
    }
    return audioEl;
};

/** Play a theme sound by URL using HTML5 Audio (no CORS, no fetch). */
export const playThemeSound = (url: string): void => {
    if (!url) return;
    try {
        const el = getAudioEl();
        el.pause();
        el.src = url;
        el.currentTime = 0;
        void el.play();
    } catch (err) {
        console.warn('Theme sound play failed:', err);
    }
};

/** Stop any currently-playing theme sound. */
export const stopThemeSound = (): void => {
    if (audioEl) {
        audioEl.pause();
        audioEl.currentTime = 0;
    }
};

/** Get the first (best) themed sound for a theme, or null. */
export const getDefaultThemeSound = (themeId: string): ThemeSound | null => {
    const sounds = THEME_SOUNDS[themeId];
    return sounds && sounds.length > 0 ? sounds[0] : null;
};
